/**
 * Created by Abner Sui on 05/22/2019.
 * Description:
 *
 * ------ maintenance history ------
 */

import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of, forkJoin } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';

import { smartEffect } from '..';
import { FileViewListActionTypes, FileViewListReload, FileViewListLoadMore } from '../actions/file-view.actions';

import { DataList } from '../../tamalelibs/models/data-list.model';
import { FileViewService } from '../../tamalelibs/services/file-view.service';
import { DocumentEntry } from '../../tamalelibs/models/document-entry.model';

@Injectable()
export class FileViewEffects {
    constructor(
        private _actions$: Actions,
        private _fileViewService: FileViewService
    ) { }

    reloadFileViewListEffect$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(FileViewListActionTypes.RELOAD),
        switchMap((action: FileViewListReload) => {
            const payload = action.payload;
            const pageIndex = payload.pageIndex;
            const pageSize = payload.pageSize;
            const sortBy = payload.sortBy;

            const sortOrder = payload.sortOrder || 'desc';

            return this._fileViewService.queryByParams(pageIndex, pageSize, sortBy, sortOrder, payload.searchText).pipe(
                map(response => {
                    const fileList = this._parseFileList(response);
                    const matchedAttachmentSet: Set<string> = new Set();
                    if (payload.searchText) {
                        response['hits']['hits'].forEach(item => matchedAttachmentSet.add(item));
                    }
                    return smartEffect(action, {
                        type: FileViewListActionTypes.RELOAD_SUCCESS,
                        payload: {
                            fileList: fileList,
                            matchedAttachmentSet: matchedAttachmentSet,
                        }
                    });
                }),
                catchError(err => {
                    return of(smartEffect(action, {
                        type: FileViewListActionTypes.RELOAD_FAILED,
                        payload: err.message,
                    }));
                })
            );
        })
    ));

    loadMoreFileViewListEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(FileViewListActionTypes.LOAD_MORE),
        switchMap((action: FileViewListLoadMore) => {
            const payload = action.payload;
            const pageIndex = payload.pageIndex;
            const pageSize = payload.pageSize;
            const sortBy = payload.sortBy;

            const sortOrder = payload.sortOrder || 'desc';

            const observables$ = [];
            observables$.push(this._fileViewService.queryByParams(pageIndex - 1, pageSize, sortBy, sortOrder, payload.searchText));
            observables$.push(this._fileViewService.queryByParams(pageIndex, pageSize, sortBy, sortOrder, payload.searchText));

            return forkJoin(observables$).pipe(
                map((responses: any) => {
                    // combine two response
                    const response = {
                        count: responses[1].count,
                        next: responses[1].next
                    };
                    response['entry-list'] = responses[0]['entry-list'].concat(responses[1]['entry-list']);
                    return response;
                }),
                map(response => {
                    const fileList = this._parseFileList(response);
                    const matchedAttachmentSet: Set<string> = new Set();
                    if (payload.searchText && response['hits'] && response['hits']['hits']) {
                        response['hits']['hits'].forEach(item => matchedAttachmentSet.add(item));
                    }
                    return smartEffect(action, {
                        type: FileViewListActionTypes.LOAD_MORE_SUCCESS,
                        payload: {
                            pageIndex: pageIndex,
                            pageSize: pageSize,
                            files: fileList,
                            matchedAttachmentSet: matchedAttachmentSet,
                        }
                    });
                })
            );
        })
    ));

    private _parseFileList(response) {
        const result: DataList<DocumentEntry> = new DataList<DocumentEntry>();

        result.totalCount = response.count;
        result.hasNext = !!response.next;
        response['entry-list'].forEach(element => {
            const one = DocumentEntry.parse(element);
            result.push(one);
        });

        return result;
    }

}
